import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { queryClient } from "@/lib/query-client.ts";
import { getAdminUrl } from "@/lib/utils.ts";
import type { MemoryUpdateReq } from "@/types/memory.ts";
import { useMutation } from "@tanstack/react-query";
import { CircleDashed, FileText, Trash2 } from "lucide-react";
import { useState } from "react";
import { deleteMemory, updateMemory } from "./api.ts";
import type { Memory } from "./types.ts";

/** Marks an unconfirmed memory as confirmed. */
export const ConfirmMemoryButton = ({ memory }: { memory: Memory }) => {
  const { mutate: updateMemoryMutation } = useMutation({
    mutationFn: (data: MemoryUpdateReq) => updateMemory(memory.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memories"] });
    },
  });

  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          title="Confirm memory"
          onClick={() => updateMemoryMutation({ confirmed: true })}
          data-statsig-name="memories-confirm"
        >
          <CircleDashed className="size-5 text-gray-500" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Confirm memory</TooltipContent>
    </Tooltip>
  );
};

/** Delete button with a confirmation dialog. Closes the surrounding editor on success. */
export const DeleteMemoryButton = ({
  memory,
  onDeleted,
}: {
  memory: Memory;
  onDeleted?: () => void;
}) => {
  const [isDeletingDialogOpen, setIsDeletingDialogOpen] = useState(false);

  const { mutate: deleteMemoryMutation, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteMemory(memory.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      setIsDeletingDialogOpen(false);
      onDeleted?.();
    },
  });

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        disabled={isDeleting}
        title="Delete memory"
        onClick={() => setIsDeletingDialogOpen(true)}
        data-statsig-name="memories-delete"
      >
        <Trash2 className="size-4" />
        Delete
      </Button>
      <Dialog open={isDeletingDialogOpen} onOpenChange={setIsDeletingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this memory</DialogTitle>
          </DialogHeader>
          <Separator />
          <DialogDescription>
            This will permanently remove this Memory from your profile. You can't undo this action.
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => deleteMemoryMutation()}
              disabled={isDeleting}
              data-statsig-name="memories-delete-confirm"
            >
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsDeletingDialogOpen(false)}
              data-statsig-name="memories-delete-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Admin-only link to the memory's ent in the admin graph viewer. */
export const ViewMemoryButton = ({ memoryId }: { memoryId: string }) => {
  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => window.open(`${getAdminUrl()}/admin/graph/ent/${memoryId}`, "_blank")}
          title="View memory"
          data-statsig-name="memory-view-ent"
        >
          <FileText className="size-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">View memory</TooltipContent>
    </Tooltip>
  );
};
